/**
 * Mode édition de la page exercice : formulaire complet (infos, vidéo,
 * correction, indices, mots-clés, statut).
 */

import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { BookExercise } from '../../../types';
import VideoPlayer from '../../common/VideoPlayer';
import { formatSecondsToMMSS } from '../BookExerciseForm';
import { TagListEditor } from './TagListEditor';
import { TagListEditors } from './useTagListEditors';
import { ExerciseFieldChange, ExerciseFormData } from './types';

interface FieldsProps {
  formData: ExerciseFormData;
  handleChange: ExerciseFieldChange;
}

/** Numéro, difficulté, points et titre de l'exercice. */
const ExerciseMainFields: React.FC<FieldsProps> = ({ formData, handleChange }) => (
  <>
    <Grid item xs={12} sm={4}>
      <TextField
        fullWidth
        label="Numéro de l'exercice"
        type="number"
        value={formData.exerciseNumber || 1}
        onChange={(e) => handleChange('exerciseNumber', parseInt(e.target.value) || 1)}
        required
        inputProps={{ min: 1 }}
      />
    </Grid>

    <Grid item xs={12} sm={4}>
      <FormControl fullWidth required>
        <InputLabel>Difficulté</InputLabel>
        <Select
          value={formData.difficulty || 'medium'}
          label="Difficulté"
          onChange={(e) => handleChange('difficulty', e.target.value as BookExercise['difficulty'])}
        >
          <MenuItem value="easy">Facile</MenuItem>
          <MenuItem value="medium">Moyen</MenuItem>
          <MenuItem value="hard">Difficile</MenuItem>
        </Select>
      </FormControl>
    </Grid>

    <Grid item xs={12} sm={4}>
      <TextField
        fullWidth
        label="Points"
        type="number"
        value={formData.points || 10}
        onChange={(e) => handleChange('points', parseInt(e.target.value) || 10)}
        required
        inputProps={{ min: 1, max: 100 }}
        helperText="Entre 1 et 100"
      />
    </Grid>

    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Titre"
        value={formData.title || ''}
        onChange={(e) => handleChange('title', e.target.value)}
        required
        helperText="Minimum 3 caractères"
      />
    </Grid>
  </>
);

/** URL et durée de la vidéo de correction. */
const ExerciseVideoUrlFields: React.FC<FieldsProps> = ({ formData, handleChange }) => (
  <>
    {/* Video Section */}
    <Grid item xs={12} md={8}>
      <TextField
        fullWidth
        label="URL de la vidéo de correction (optionnel)"
        value={formData.url || ''}
        onChange={(e) => handleChange('url', e.target.value)}
        placeholder="https://www.youtube.com/watch?v=... ou URL Firebase"
        helperText="Lien vers la vidéo explicative de la correction"
      />
    </Grid>

    {/* Durée de la vidéo — auto au rendu, corrigeable à la main */}
    <Grid item xs={12} md={4}>
      <TextField
        fullWidth
        type="number"
        label="Durée de la vidéo (secondes)"
        value={formData.videoDuration ?? ''}
        onChange={(event) =>
          handleChange(
            'videoDuration',
            event.target.value === '' ? undefined : Math.max(0, parseInt(event.target.value, 10) || 0)
          )
        }
        inputProps={{ min: 0, max: 86400 }}
        helperText={
          formData.videoDuration
            ? `≈ ${formatSecondsToMMSS(formData.videoDuration)} — auto au rendu, corrigeable`
            : 'Renseignée auto à la génération ; saisissez-la si absente'
        }
      />
    </Grid>
  </>
);

/** Prévisualisation vidéo, résumé et correction de l'exercice. */
const ExerciseVideoAndCorrectionFields: React.FC<FieldsProps> = ({ formData, handleChange }) => (
  <>
    {/* Video Preview */}
    {formData.url && (
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Prévisualisation de la vidéo
        </Typography>
        <VideoPlayer
          youtubeUrl={formData.url}
          title={formData.title || 'Vidéo de correction'}
          aspectRatio="16/9"
        />
      </Grid>
    )}

    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Résumé de la vidéo (optionnel)"
        value={formData.resume || ''}
        onChange={(e) => handleChange('resume', e.target.value)}
        multiline
        rows={4}
        helperText="Résumé écrit du contenu de la vidéo de correction"
      />
    </Grid>

    {/* Correction Section */}
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Correction de l'exercice"
        value={formData.correction || ''}
        onChange={(e) => handleChange('correction', e.target.value)}
        multiline
        rows={8}
        helperText="Solution détaillée et étapes de résolution de l'exercice"
      />
    </Grid>
  </>
);

/** Sélecteur du statut (brouillon/publié). */
const ExerciseStatusField: React.FC<FieldsProps> = ({ formData, handleChange }) => (
  <Grid item xs={12}>
    <FormControl fullWidth>
      <InputLabel>Statut</InputLabel>
      <Select
        value={formData.status || 'draft'}
        label="Statut"
        onChange={(e) => handleChange('status', e.target.value as BookExercise['status'])}
      >
        <MenuItem value="draft">Brouillon</MenuItem>
        <MenuItem value="published">Publié</MenuItem>
      </Select>
    </FormControl>
  </Grid>
);

interface ExerciseEditFormProps extends FieldsProps {
  tags: TagListEditors;
}

/** Formulaire d'édition/création d'un exercice. */
export const ExerciseEditForm: React.FC<ExerciseEditFormProps> = ({ formData, handleChange, tags }) => (
  <Paper elevation={2} sx={{ p: 3 }}>
    <Typography variant="h6" gutterBottom>
      Informations de l'exercice
    </Typography>
    <Divider sx={{ mb: 3 }} />

    <Grid container spacing={2}>
      <ExerciseMainFields formData={formData} handleChange={handleChange} />
      <ExerciseVideoUrlFields formData={formData} handleChange={handleChange} />
      <ExerciseVideoAndCorrectionFields formData={formData} handleChange={handleChange} />

      {/* Hints Section */}
      <Grid item xs={12}>
        <TagListEditor
          title="Indices (optionnels)"
          placeholder="Ajouter un indice"
          value={tags.newHint}
          onValueChange={tags.setNewHint}
          onAdd={tags.handleAddHint}
          items={formData.hints || []}
          onRemove={tags.handleRemoveHint}
          color="primary"
        />
      </Grid>

      {/* Keywords Section */}
      <Grid item xs={12}>
        <TagListEditor
          title="Mots-clés (optionnels)"
          placeholder="Ajouter un mot-clé"
          value={tags.newKeyword}
          onValueChange={tags.setNewKeyword}
          onAdd={tags.handleAddKeyword}
          items={formData.keywords || []}
          onRemove={tags.handleRemoveKeyword}
          color="secondary"
        />
      </Grid>

      <ExerciseStatusField formData={formData} handleChange={handleChange} />
    </Grid>
  </Paper>
);
